import { AxiosResponse, InternalAxiosRequestConfig, isCancel } from 'axios';
import { LLog } from './LLog';
import { sentry } from '../../sentry/sentry.singleton';

export interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  startTime?: number;
  isPublicAsset?: boolean;
  redactLog?: boolean;
}

export interface ExtendedAxiosError extends Error {
  config?: ExtendedAxiosRequestConfig;
  response?: AxiosResponse;
}

interface SentryInstance {
  getCurrentHub(): {
    getScope():
      | { getTransaction(): { traceId: string; spanId: string } | undefined }
      | undefined;
  };
}

/**
 * The address a log line prints. With `redactLog` the last segment of the path
 * is left out, and the query with it: the anonymous email check carries the
 * address a person typed there (BUG-800), and errors print in production.
 */
export function toLoggedUrl(url: string, redactLog?: boolean): string {
  if (!redactLog) return url;
  const [path] = url.split(/[?#]/);
  return path.replace(/[^/]+\/?$/, '[redacted]');
}

export const LLogMiddleware = {
  request: (config: InternalAxiosRequestConfig) => {
    const startTime = performance.now();
    const requestId = crypto.randomUUID();
    const method = config.method?.toUpperCase() || 'UNKNOWN';

    const { isPublicAsset, redactLog } = config as ExtendedAxiosRequestConfig;
    const url = toLoggedUrl(config.url || 'unknown', redactLog);

    LLog.debug(
      {
        component: 'http',
        method,
        operation: 'IncomingRequest',
      },
      `[${method}] ${url}`,
      // A request that carries a credential or personal data (BUG-800: the
      // invitation link's token, a name and a phone) asks for both to stay out.
      redactLog
        ? { requestId }
        : {
            headers: config.headers,
            body: config.data,
            requestId,
          },
    );

    if (isPublicAsset) {
      /*
       * A cross-origin GET only skips the CORS preflight while it stays a
       * "simple request": every header outside the safelist forces an OPTIONS
       * first. The WhatsApp media proxy answers that OPTIONS itself and its
       * response carries no Access-Control-Allow-Origin, so the browser blocks
       * the download and the attachment falls back to the raw .enc file
       * (BUG-769). The route is an unauthenticated pass-through, so these
       * headers buy nothing — and the gateway forwards them to the upstream
       * CDN, which would leak the user's credentials to a third party.
       */
      config.headers.delete('Authorization');
      config.headers.delete('X-Api-Key');
      config.headers.delete('Content-Type');
    } else {
      config.headers.set('X-Correlation-ID', LLog.getCorrelationId());
      config.headers.set('X-Request-ID', requestId);
    }

    try {
      const hub = (sentry as unknown as SentryInstance).getCurrentHub();
      const scope = hub.getScope();
      const transaction = scope?.getTransaction();
      if (transaction && !isPublicAsset) {
        const traceId = transaction.traceId;
        const spanId = transaction.spanId;
        config.headers.set('X-Trace-ID', traceId);
        config.headers.set('X-Span-ID', spanId);
      }
    } catch (error) {
      LLog.warn(
        {
          component: 'http',
          method,
          operation: 'IncomingRequest',
        },
        'Failed to get Sentry transaction',
        { error: String(error) },
      );
    }

    (config as ExtendedAxiosRequestConfig).startTime = startTime;

    return config;
  },

  response: (response: AxiosResponse) => {
    const config = response.config as ExtendedAxiosRequestConfig;
    const duration = performance.now() - (config.startTime || 0);
    const method = response.config.method?.toUpperCase() || 'UNKNOWN';
    const url = toLoggedUrl(response.config.url || 'unknown', config.redactLog);

    LLog.info(
      {
        component: 'http',
        method,
        operation: 'Response',
      },
      `[${method}] ${url} (${response.status}) in ${duration.toFixed(2)}ms`,
      {
        status: response.status,
        statusText: response.statusText,
        url,
      },
      {
        duration,
        memory: 0,
      },
    );

    return response;
  },

  error: (error: ExtendedAxiosError) => {
    // Canceled requests (chat-switch cleanup, rapid nav, StrictMode) are expected; don't log them.
    if (isCancel(error)) {
      return Promise.reject(error);
    }

    const config = error.config;
    const duration = performance.now() - (config?.startTime || 0);
    const method = config?.method?.toUpperCase() || 'UNKNOWN';
    const url = toLoggedUrl(config?.url || 'unknown', config?.redactLog);
    const status = error.response?.status;

    LLog.error(
      {
        component: 'http',
        method,
        operation: 'Error',
      },
      `[${method}] ${url} (${status || 'NO_STATUS'}) failed after ${duration.toFixed(2)}ms`,
      error,
      {
        status,
        statusText: error.response?.statusText,
        url,
      },
      {
        duration,
        memory: 0,
      },
    );

    return Promise.reject(error);
  },
};
